import {
	Button,
	Checkbox,
	createMuiTheme,
	FormControlLabel,
	MuiThemeProvider,
	TextField,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
	Card,
	CardBody,
	CardFooter,
	CardHeader,
	CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import {
	deleteSupplier,
	getSupplierById,
	postSupplier,
	updateSupplier,
} from "../../../../api/supplier";
import { getSupplierTypes } from "../../../../api/supplierType";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { useSkeleton } from "../../../hooks/useSkeleton";

// Create theme for delete button (red)
const theme = createMuiTheme({
	palette: {
		secondary: {
			main: "#F64E60",
		},
	},
});

function getEmptySupplier() {
	return {
		nombre: null,
		razon_social: null,
		tipo_proveedor_id: null,
		nif: null,
		direccion: null,
		codigo_postal: null,
		poblacion: null,
		provincia: null,
		pais: null,
		web: null,
		contacto_nombre: null,
		contacto_email: null,
		contacto_telefono_fijo: null,
		contacto_telefono_movil: null,
		contacto_fax: null,
		activo: true,
	};
}

export default function EditSuppliersPage() {
	const [supplier, setSupplier] = useState(getEmptySupplier());
	const [supplierTypes, setSupplierTypes] = useState([]);

	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

	const supplierId = useParams().id;
	const history = useHistory();
	const location = useLocation();

	const loggedUser = useSelector(
		(store) => store.authentication?.user,
		shallowEqual
	);

	const handleBack = () => {
		const prevTablePage = location.state?.page;

		history.push(
			`/proveedores${prevTablePage ? "?page=" + prevTablePage : ""}`
		);
	};

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		getSupplierTypes(loggedUser?.accessToken)
			.then((res) => {
				if (res.status === 200) {
					setSupplierTypes(res.data);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage:
						"No se han podido obtener los tipos de proveedores.",
				});
				handleBack();
			});
		if (!supplierId) {
			disableLoadingData();
			return;
		}
		getSupplierById(supplierId, loggedUser?.accessToken)
			.then((res) => {
				if (res.status === 200) {
					setSupplier(res.data);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: "No se ha podido obtener el proveedor.",
				});
				handleBack();
			});
	}, [supplierId, disableLoadingData, history]);

	function saveSupplier() {
		if (!supplierId) {
			postSupplier(supplier, loggedUser?.accessToken)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({
							title: "¡Guardado!",
							customMessage: "Proveedor guardado con éxito.",
						});
						handleBack();
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "No se ha podido guardar el proveedor.",
					});
				});
		} else {
			updateSupplier(supplierId, supplier, loggedUser?.accessToken)
				.then((res) => {
					if (res.status === 204) {
						alertSuccess({
							title: "¡Guardado!",
							customMessage: "Cambios guardados con éxito.",
						});
						handleBack();
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "No se han podido guardar los cambios.",
					});
				});
		}
	}

	const handleChange = (element) => (event) => {
		setSupplier({ ...supplier, [element]: event.target.value });
	};

	if (isLoadingData) return <ContentSkeleton />;
	else
		return (
			<>
				<Card>
					<CardHeader title="Proveedor">
						<CardHeaderToolbar>
							<Button
								onClick={() => saveSupplier()}
								variant="outlined"
								color="primary"
								style={{
									marginRight: supplierId ? "20px" : "",
								}}
							>
								Guardar proveedor
							</Button>
							{supplierId && (
								<>
									<MuiThemeProvider theme={theme}>
										<Button
											className="py-2"
											onClick={() =>
												setOpenConfirmDialog(true)
											}
											variant="outlined"
											color="secondary"
										>
											Eliminar proveedor
										</Button>
									</MuiThemeProvider>
								</>
							)}
						</CardHeaderToolbar>
					</CardHeader>
					<CardBody>
						<TextField
							id={`nombre`}
							label="Nombre Comercial"
							value={supplier.nombre}
							onChange={handleChange("nombre")}
							InputLabelProps={{
								shrink: true,
							}}
							margin="normal"
							variant="outlined"
							required
						/>
						<div className="row d-flex align-items-center">
							<div className="col-md-4">
								<TextField
									id={`razon_social`}
									label="Razón Social"
									value={supplier.razon_social}
									onChange={handleChange("razon_social")}
									InputLabelProps={{
										shrink: true,
									}}
									margin="normal"
									variant="outlined"
								/>
							</div>
							<div className="col-md-4">
								<TextField
									id={`nif`}
									label="NIF"
									value={supplier.nif}
									onChange={handleChange("nif")}
									InputLabelProps={{
										shrink: true,
									}}
									margin="normal"
									variant="outlined"
								/>
							</div>
							<div className="col-md-4">
								<Autocomplete
									id="autocomplete-tipo-proveedor"
									disablePortal
									disableClearable
									filterSelectedOptions
									options={supplierTypes}
									getOptionLabel={(option) =>
										option.descripcion
									}
									value={
										supplierTypes?.find(
											(x) =>
												x.id ===
												supplier?.tipo_proveedor_id
										) || ""
									}
									onChange={(event, selected) => {
										setSupplier({
											...supplier,
											tipo_proveedor_id: selected
												? selected.id
												: null,
										});
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Tipo de proveedor"
											margin="normal"
											variant="outlined"
											InputLabelProps={{
												shrink: true,
											}}
										/>
									)}
								/>
							</div>
						</div>
						<div className="row d-flex align-items-center">
							<div className="col-md-4">
								<TextField
									id={`direccion`}
									label="Dirección"
									value={supplier.direccion}
									onChange={handleChange("direccion")}
									InputLabelProps={{
										shrink: true,
									}}
									margin="normal"
									variant="outlined"
								/>
							</div>
							<div className="col-md-4">
								<TextField
									id={`codigo_postal`}
									label="Código Postal"
									value={supplier.codigo_postal}
									onChange={handleChange("codigo_postal")}
									InputLabelProps={{
										shrink: true,
									}}
									margin="normal"
									variant="outlined"
								/>
							</div>
							<div className="col-md-4">
								<TextField
									id={`poblacion`}
									label="Población"
									value={supplier.poblacion}
									onChange={handleChange("poblacion")}
									InputLabelProps={{
										shrink: true,
									}}
									margin="normal"
									variant="outlined"
								/>
							</div>
						</div>
						<div className="row d-flex align-items-center">
							<div className="col-md-6">
								<TextField
									id={`provincia`}
									label="Provincia"
									value={supplier.provincia}
									onChange={handleChange("provincia")}
									InputLabelProps={{
										shrink: true,
									}}
									margin="normal"
									variant="outlined"
								/>
							</div>
							<div className="col-md-6">
								<TextField
									id={`pais`}
									label="País"
									value={supplier.pais}
									onChange={handleChange("pais")}
									InputLabelProps={{
										shrink: true,
									}}
									margin="normal"
									variant="outlined"
								/>
							</div>
						</div>
						<div className="row d-flex align-items-center">
							<div className="col-md-4">
								<TextField
									id={`web`}
									label="Web"
									value={supplier.web}
									onChange={handleChange("web")}
									InputLabelProps={{
										shrink: true,
									}}
									margin="normal"
									variant="outlined"
								/>
							</div>
							<div className="col-md-4">
								<TextField
									id={`contacto_nombre`}
									label="Contacto Nombre"
									value={supplier.contacto_nombre}
									onChange={handleChange("contacto_nombre")}
									InputLabelProps={{
										shrink: true,
									}}
									margin="normal"
									variant="outlined"
								/>
							</div>
							<div className="col-md-4">
								<TextField
									id={`contacto_email`}
									label="Contacto Email"
									value={supplier.contacto_email}
									onChange={handleChange("contacto_email")}
									InputLabelProps={{
										shrink: true,
									}}
									margin="normal"
									variant="outlined"
								/>
							</div>
						</div>
						<div className="row d-flex align-items-center">
							<div className="col-md-4">
								<TextField
									id={`contacto_telefono_fijo`}
									label="Contacto Teléfono Fijo"
									value={supplier.contacto_telefono_fijo}
									onChange={handleChange(
										"contacto_telefono_fijo"
									)}
									InputLabelProps={{
										shrink: true,
									}}
									margin="normal"
									variant="outlined"
								/>
							</div>
							<div className="col-md-4">
								<TextField
									id={`contacto_telefono_movil`}
									label="Contacto Teléfono Móvil"
									value={supplier.contacto_telefono_movil}
									onChange={handleChange(
										"contacto_telefono_movil"
									)}
									InputLabelProps={{
										shrink: true,
									}}
									margin="normal"
									variant="outlined"
								/>
							</div>
							<div className="col-md-4">
								<TextField
									id={`contacto_fax`}
									label="Contacto Fax"
									value={supplier.contacto_fax}
									onChange={handleChange("contacto_fax")}
									InputLabelProps={{
										shrink: true,
									}}
									margin="normal"
									variant="outlined"
								/>
							</div>
						</div>
					</CardBody>
					<CardFooter>
						<FormControlLabel
							control={
								<Checkbox
									checked={supplier.activo}
									onChange={() =>
										setSupplier({
											...supplier,
											activo: !supplier.activo,
										})
									}
									name="checkActive"
								/>
							}
							label="Activo"
						/>
					</CardFooter>
				</Card>
				<Button
					onClick={handleBack}
					variant="outlined"
					style={{ marginRight: "20px" }}
				>
					Atrás
				</Button>
				<Button
					onClick={() => saveSupplier()}
					variant="outlined"
					color="primary"
					style={{ marginRight: supplierId ? "20px" : "" }}
				>
					Guardar proveedor
				</Button>
				{supplierId && (
					<>
						<MuiThemeProvider theme={theme}>
							<Button
								className="py-2"
								onClick={() => setOpenConfirmDialog(true)}
								variant="outlined"
								color="secondary"
							>
								Eliminar proveedor
							</Button>
						</MuiThemeProvider>

						<ConfirmDialog
							title={
								"¿Estás seguro de que quieres eliminar este proveedor?"
							}
							open={openConfirmDialog}
							setOpen={setOpenConfirmDialog}
							onConfirm={() => {
								deleteSupplier(
									supplierId,
									loggedUser?.accessToken
								)
									.then((res) => {
										if (
											res.status === 204 ||
											res.status === 200
										) {
											alertSuccess({
												title: "¡Eliminado!",
												customMessage:
													"Proveedor eliminado con éxito",
											});
											handleBack();
										}
									})
									.catch((error) => {
										alertError({
											error: error,
											customMessage:
												"No se ha podido eliminar el proveedor.",
										});
									});
							}}
						/>
					</>
				)}
			</>
		);
}
